import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  ArrowRight, Car, Phone, MapPin, Gauge, Fuel, Settings2,
  Star, Quote, ChevronRight, ChevronLeft, MessageCircle,
  CalendarCheck, Building2, Clock
} from "lucide-react";
import { HERO_BG, getIcon, formatHours, track } from "@/components/landing/shared";
import { getImageUrl } from "@/utils/imageUrl";

const API = process.env.REACT_APP_BACKEND_URL;

export default function LandingPage() {
  const [vehicles, setVehicles] = useState([]);
  const [config, setConfig] = useState(null);
  const [content, setContent] = useState(null);
  const [centers, setCenters] = useState([]);
  const [heroSlides, setHeroSlides] = useState([]);
  const [heroConfig, setHeroConfig] = useState({ autoplay: true, autoplay_interval: 5000, show_indicators: true, show_arrows: true });
  const [currentSlide, setCurrentSlide] = useState(0);
  const [generalContact, setGeneralContact] = useState(null);
  const [promotions, setPromotions] = useState([]);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/api/vehicles`, { params: { status: "disponible" } }).then(r => r.data.slice(0, 6)).catch(() => []),
      axios.get(`${API}/api/config`).then(r => r.data).catch(() => null),
      axios.get(`${API}/api/site-content`).then(r => r.data).catch(() => null),
      axios.get(`${API}/api/centers?active_only=true`).then(r => r.data).catch(() => []),
      axios.get(`${API}/api/hero-slides?active_only=true`).then(r => r.data).catch(() => []),
      axios.get(`${API}/api/hero-config`).then(r => r.data).catch(() => ({})),
      axios.get(`${API}/api/public/general-contact`).then(r => r.data).catch(() => null),
      axios.get(`${API}/api/promotions/public?home_only=true`).then(r => r.data).catch(() => []),
    ]).then(([veh, cfg, cnt, ctr, slides, hCfg, gc, prms]) => {
      setVehicles(veh); setConfig(cfg); setContent(cnt); setCenters(ctr);
      setHeroSlides(slides);
      if (hCfg) setHeroConfig(prev => ({ ...prev, ...hCfg }));
      setGeneralContact(gc);
      setPromotions(prms);
    });
  }, []);

  useEffect(() => {
    if (!heroConfig.autoplay || heroSlides.length <= 1) return;
    const timer = setInterval(() => setCurrentSlide(prev => (prev + 1) % heroSlides.length), heroConfig.autoplay_interval);
    return () => clearInterval(timer);
  }, [heroConfig.autoplay, heroConfig.autoplay_interval, heroSlides.length]);

  const goToSlide = useCallback((idx) => setCurrentSlide(idx), []);
  const prevSlide = useCallback(() => setCurrentSlide(prev => (prev - 1 + heroSlides.length) % heroSlides.length), [heroSlides.length]);
  const nextSlide = useCallback(() => setCurrentSlide(prev => (prev + 1) % heroSlides.length), [heroSlides.length]);

  if (!config || !content) {
    return <div className="flex items-center justify-center min-h-screen bg-white"><div className="animate-spin h-8 w-8 border-2 border-slate-900 border-t-transparent rounded-full" /></div>;
  }

  const hero = content.hero || {};
  const services = content.services || [];
  const advantages = content.advantages || [];
  const testimonials = content.testimonials || [];
  const ctaFinal = content.cta_final || {};
  const quickAccess = content.quick_access || [];
  const trustStats = content.trust_stats || [];
  const companyName = config.company_name || "ASCAUTO";
  const tagline = config.tagline || "";
  const vis = config.sections_visibility || {};
  const show = (key) => vis[key] !== false;
  const waPhoneClean = generalContact?.whatsapp_phone_clean || "";
  const waMessage = generalContact?.whatsapp_message || "";
  const buildWaHref = (msg) => {
    if (!waPhoneClean) return "#";
    const base = `https://wa.me/${waPhoneClean}`;
    const text = msg || waMessage;
    return text ? `${base}?text=${encodeURIComponent(text)}` : base;
  };

  return (
    <div className="bg-white">

      {/* ============================================= */}
      {/* 1. HERO DINAMICO                              */}
      {/* ============================================= */}
      {show("hero") && (
        <section className="relative min-h-[92vh] flex items-center overflow-hidden" data-testid="hero-section">
          {heroSlides.length > 0 ? heroSlides.map((slide, idx) => (
            <div
              key={slide.id}
              className="absolute inset-0"
              style={{
                opacity: idx === currentSlide ? 1 : 0,
                zIndex: idx === currentSlide ? 1 : 0,
                transition: "opacity 1s ease-in-out",
              }}
              data-testid={`hero-slide-${idx}`}
            >
              {/* Iter 213/214 — Imagen responsive + modo de ajuste configurable.
                  image_fit: cover (recorta, ideal foto), contain (sin recortar,
                  ideal cartel), auto (cover si overlay activo, contain si no). */}
              {(() => {
                const fitMode = slide.image_fit || "cover";
                const resolvedFit = fitMode === "auto"
                  ? (slide.overlay_enabled === false ? "contain" : "cover")
                  : fitMode;
                const isContain = resolvedFit === "contain";
                return (
                  <>
                    {/* Fondo oscuro para bandas cuando contain */}
                    {isContain && <div className="absolute inset-0 bg-slate-900" />}
                    <picture>
                      {slide.image_mobile_url && (
                        <source media="(max-width: 768px)" srcSet={getImageUrl(slide.image_mobile_url)} />
                      )}
                      <img
                        src={getImageUrl(slide.image_url) || HERO_BG} alt=""
                        className={`relative w-full h-full ${isContain ? "object-contain" : "object-cover"}`}
                        data-testid={`hero-slide-img-${idx}`}
                        data-fit={resolvedFit}
                        style={{ transform: idx === currentSlide ? "scale(1.03)" : "scale(1)", transition: "transform 8s ease-out" }}
                      />
                    </picture>
                    {/* Overlay gradient solo si NO es contain (en contain las bandas
                        ya son oscuras y un gradient encima oscurece la creatividad). */}
                    {!isContain && (
                      <div className="absolute inset-0" style={{ background: `linear-gradient(135deg, rgba(10,15,30,${(slide.overlay_opacity || 75) / 100}) 0%, rgba(10,15,30,${((slide.overlay_opacity || 75) - 15) / 100}) 100%)` }} />
                    )}
                  </>
                );
              })()}
            </div>
          )) : (
            <div className="absolute inset-0">
              <img src={getImageUrl(hero.bg_image) || HERO_BG} alt="" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-br from-slate-900/85 to-slate-900/70" />
            </div>
          )}

          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 w-full">
            {heroSlides.length > 0 ? heroSlides.map((slide, idx) => {
              // Iter 211: si overlay_enabled === false → solo imagen, nada encima.
              // El fallback a la tagline global se ELIMINA: si el slide no tiene
              // aux_text propio, no se pinta badge (antes mostraba "SERVICIOS OFICIALES STELLANTIS").
              const overlayOff = slide.overlay_enabled === false;
              const hasAux = !overlayOff && !!(slide.aux_text && slide.aux_text.trim());
              const hasTitle = !overlayOff && !!(slide.title && slide.title.trim());
              const hasSubtitle = !overlayOff && !!(slide.subtitle && slide.subtitle.trim());
              const hasCtaPrimary = !overlayOff && !!(slide.cta_primary_text && slide.cta_primary_text.trim());
              const hasCtaSecondary = !overlayOff && !!(slide.cta_secondary_text && slide.cta_secondary_text.trim());
              return (
              <div
                key={slide.id}
                className="max-w-2xl"
                data-testid={`hero-slide-overlay-${idx}`}
                style={{
                  opacity: idx === currentSlide ? 1 : 0,
                  transform: idx === currentSlide ? "translateY(0)" : "translateY(20px)",
                  position: idx === currentSlide ? "relative" : "absolute",
                  pointerEvents: idx === currentSlide ? "auto" : "none",
                  visibility: idx === currentSlide ? "visible" : "hidden",
                  transition: "opacity 700ms ease-out, transform 700ms ease-out",
                }}
              >
                {hasAux && (
                  <div className="flex items-center gap-3 mb-6">
                    <div className="h-px flex-1 max-w-[40px] bg-red-600" />
                    <span className="text-red-400 text-[11px] font-semibold uppercase tracking-[0.3em]">{slide.aux_text}</span>
                  </div>
                )}
                {hasTitle && (
                  <h1 className="heading-industrial text-4xl sm:text-5xl lg:text-7xl font-extrabold leading-[0.95] mb-6" style={{ color: slide.text_color === "dark" ? "#0f172a" : "#ffffff" }} data-testid="hero-title">
                    {slide.title}
                  </h1>
                )}
                {hasSubtitle && (
                  <p className="text-base sm:text-lg leading-relaxed max-w-xl mb-10" style={{ color: slide.text_color === "dark" ? "#475569" : "#cbd5e1" }}>
                    {slide.subtitle}
                  </p>
                )}
                {(hasCtaPrimary || hasCtaSecondary) && (
                  <div className="flex flex-col sm:flex-row gap-3">
                    {hasCtaPrimary && (
                      <Link to={slide.cta_primary_link || "/cita"} onClick={() => track("hero_cta_primary", slide.cta_primary_text)}>
                        <Button className="bg-red-600 hover:bg-red-700 text-white rounded-none text-sm uppercase tracking-wider font-bold h-12 px-8 shadow-lg shadow-red-600/20 transition-all hover:shadow-xl hover:shadow-red-600/30" data-testid="hero-cta-primary">
                          {slide.cta_primary_text} <ArrowRight className="w-4 h-4 ml-2" />
                        </Button>
                      </Link>
                    )}
                    {hasCtaSecondary && (
                      <Link to={slide.cta_secondary_link || "/vehiculos"} onClick={() => track("hero_cta_secondary", slide.cta_secondary_text)}>
                        <Button variant="outline" className="border-white/30 text-white hover:bg-white/10 hover:border-white/50 rounded-none text-sm uppercase tracking-wider font-bold h-12 px-8 transition-all" data-testid="hero-cta-secondary">
                          {slide.cta_secondary_text}
                        </Button>
                      </Link>
                    )}
                  </div>
                )}
              </div>
              );
            }) : (
              <div className="max-w-2xl">
                <h1 className="heading-industrial text-4xl sm:text-5xl lg:text-7xl font-extrabold text-white leading-[0.95] mb-6" data-testid="hero-title">{hero.title || companyName}</h1>
                <p className="text-base sm:text-lg text-slate-300 leading-relaxed max-w-xl mb-10">{hero.subtitle || ""}</p>
                <div className="flex flex-col sm:flex-row gap-3">
                  <Link to="/cita" onClick={() => track("hero_cta_primary", "Reservar Cita")}><Button className="bg-red-600 hover:bg-red-700 text-white rounded-none text-sm uppercase tracking-wider font-bold h-12 px-8">Reservar Cita <ArrowRight className="w-4 h-4 ml-2" /></Button></Link>
                  <Link to="/vehiculos" onClick={() => track("hero_cta_secondary", "Ver Vehiculos")}><Button variant="outline" className="border-white/30 text-white hover:bg-white/10 rounded-none text-sm uppercase tracking-wider font-bold h-12 px-8">Ver Vehiculos</Button></Link>
                </div>
              </div>
            )}
          </div>

          {heroConfig.show_arrows && heroSlides.length > 1 && (
            <>
              <button onClick={prevSlide} className="absolute left-4 top-1/2 -translate-y-1/2 z-20 w-11 h-11 rounded-full bg-black/30 backdrop-blur-sm border border-white/10 text-white/80 hover:bg-black/50 hover:text-white flex items-center justify-center transition-all" data-testid="hero-prev-btn"><ChevronLeft className="w-5 h-5" /></button>
              <button onClick={nextSlide} className="absolute right-4 top-1/2 -translate-y-1/2 z-20 w-11 h-11 rounded-full bg-black/30 backdrop-blur-sm border border-white/10 text-white/80 hover:bg-black/50 hover:text-white flex items-center justify-center transition-all" data-testid="hero-next-btn"><ChevronRight className="w-5 h-5" /></button>
            </>
          )}
          {heroConfig.show_indicators && heroSlides.length > 1 && (
            <div className="absolute bottom-10 left-1/2 -translate-x-1/2 z-20 flex gap-2" data-testid="hero-indicators">
              {heroSlides.map((_, idx) => (
                <button key={idx} onClick={() => goToSlide(idx)} className={`h-1 rounded-full transition-all duration-500 ${idx === currentSlide ? "w-8 bg-red-500" : "w-4 bg-white/40 hover:bg-white/60"}`} data-testid={`hero-indicator-${idx}`} />
              ))}
            </div>
          )}
          <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-white to-transparent z-10" />
        </section>
      )}

      {/* ============================================= */}
      {/* 2. ACCESOS RAPIDOS                            */}
      {/* ============================================= */}
      {show("quick_access") && quickAccess.length > 0 && (
        <section className="relative -mt-20 z-20 pb-16" data-testid="quick-access-section">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4">
              {quickAccess.map((qa, i) => {
                const Icon = getIcon(qa.icon);
                const isPrimary = qa.primary;
                const hasImage = !!qa.image_url;
                const imgSrc = getImageUrl(qa.image_url);
                const isExternal = (qa.link||"").startsWith("http") || (qa.link||"").startsWith("tel:") || (qa.link||"").startsWith("mailto:");
                const Wrapper = isExternal ? "a" : Link;
                const wrapperProps = isExternal ? { href: qa.link, target: "_blank", rel: "noopener noreferrer" } : { to: qa.link || "/cita" };
                return (
                  <Wrapper
                    key={i}
                    {...wrapperProps}
                    onClick={() => track("quick_access", qa.title)}
                    className={`group flex flex-col items-center justify-center gap-3 p-5 sm:p-6 text-center shadow-lg transition-all duration-300 hover:-translate-y-1 hover:shadow-xl ${
                      isPrimary
                        ? "bg-red-600 text-white col-span-2 sm:col-span-1 shadow-red-600/20"
                        : "bg-white text-slate-900 border border-slate-100"
                    }`}
                    data-testid={`quick-access-${i}`}
                  >
                    <div className={`w-12 h-12 flex items-center justify-center transition-colors duration-300 overflow-hidden ${
                      isPrimary ? "bg-white/20 rounded-none" : "bg-slate-900/5 group-hover:bg-red-600 rounded-none"
                    }`}>
                      {hasImage
                        ? <img src={imgSrc} alt={qa.title} className="w-full h-full object-cover" />
                        : <Icon className={`w-5 h-5 ${isPrimary ? "text-white" : "text-slate-700 group-hover:text-white transition-colors duration-300"}`} />
                      }
                    </div>
                    <span className={`text-xs font-bold uppercase tracking-wider leading-tight ${isPrimary ? "" : "text-slate-800"}`}>
                      {qa.title}
                    </span>
                  </Wrapper>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* ============================================= */}
      {/* 3. SOMOS SERVICIO OFICIAL                     */}
      {/* ============================================= */}
      {show("advantages") && advantages.length > 0 && (
        <section className="py-24 lg:py-32 bg-white" data-testid="trust-authority-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-2xl mx-auto text-center mb-16 lg:mb-20">
              <div className="flex items-center justify-center gap-3 mb-5">
                <div className="h-px w-10 bg-red-600" />
                <span className="text-[11px] uppercase tracking-[0.3em] text-red-600 font-semibold">Servicio Oficial</span>
                <div className="h-px w-10 bg-red-600" />
              </div>
              <h2 className="heading-industrial text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900 uppercase leading-tight" data-testid="trust-title">
                {content.advantages_section_title || "Tu Servicio Oficial de confianza en Móstoles y Madrid"}
              </h2>
              <p className="text-sm sm:text-base text-slate-500 mt-5 leading-relaxed max-w-xl mx-auto">
                {content.advantages_section_subtitle || "Decadas de experiencia, atencion profesional, tecnologia de diagnosis y servicios especializados para el cuidado integral de tu vehiculo."}
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-slate-200">
              {advantages.sort((a, b) => (a.order || 0) - (b.order || 0)).map((a, i) => {
                const Icon = getIcon(a.icon);
                return (
                  <div key={i} className="bg-white p-8 lg:p-10 group hover:bg-slate-50 transition-colors duration-300" data-testid={`advantage-card-${i}`}>
                    <div className="w-14 h-14 bg-slate-900 flex items-center justify-center mb-5 group-hover:bg-red-600 transition-colors duration-300">
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                    <h3 className="heading-industrial text-sm font-bold text-slate-900 uppercase tracking-wider mb-2">{a.title}</h3>
                    <p className="text-sm text-slate-500 leading-relaxed">{a.description}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* ============================================= */}
      {/* 4. SERVICIOS DESTACADOS                       */}
      {/* ============================================= */}
      {show("services") && services.length > 0 && (
        <section className="py-24 lg:py-32 bg-slate-50" data-testid="services-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-xl mb-16">
              <span className="text-[11px] uppercase tracking-[0.3em] text-red-600 font-semibold">Taller Oficial</span>
              <h2 className="heading-industrial text-2xl sm:text-3xl font-bold text-slate-900 mt-3 uppercase">
                {content.services_section_title || "Servicios destacados"}
              </h2>
              <p className="text-sm text-slate-500 mt-3 leading-relaxed">
                {content.services_section_subtitle || "Protocolos oficiales, recambios originales y garantia de fabricante."}
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {services.sort((a, b) => (a.order || 0) - (b.order || 0)).map((s, i) => {
                const Icon = getIcon(s.icon);
                return (
                  <div key={i} className="bg-white border border-slate-200 p-7 flex flex-col group hover:border-slate-300 hover:shadow-md transition-all duration-300" data-testid={`service-card-${i}`}>
                    <div className="flex items-start gap-4 mb-5">
                      <div className="w-11 h-11 bg-red-600/10 flex items-center justify-center flex-shrink-0 group-hover:bg-red-600 transition-colors duration-300">
                        <Icon className="w-5 h-5 text-red-600 group-hover:text-white transition-colors duration-300" />
                      </div>
                      <div>
                        <h3 className="heading-industrial text-sm font-bold text-slate-900 uppercase tracking-wider leading-tight">{s.title}</h3>
                        <p className="text-sm text-slate-500 mt-1.5 leading-relaxed">{s.description}</p>
                      </div>
                    </div>
                    <div className="mt-auto pt-5 border-t border-slate-100">
                      <Link to="/cita" onClick={() => track("service_cta", s.title)}>
                        <Button variant="ghost" size="sm" className="text-xs uppercase tracking-wider font-semibold text-red-600 hover:text-red-700 hover:bg-red-50 h-8 px-3 rounded-none" data-testid={`service-cta-${i}`}>
                          Solicitar cita <ChevronRight className="w-3.5 h-3.5 ml-1" />
                        </Button>
                      </Link>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* ============================================= */}
      {/* 5. CONFIANZA Y TRAYECTORIA                    */}
      {/* ============================================= */}
      {show("trust") && trustStats.length > 0 && (
        <section className="py-20 lg:py-24 bg-slate-900 relative overflow-hidden" data-testid="trust-stats-section">
          <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: "repeating-linear-gradient(90deg, rgba(255,255,255,.07) 0 1px, transparent 1px 60px)" }} />
          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-14">
              <span className="text-[11px] uppercase tracking-[0.3em] text-red-500 font-semibold">{content.trust_section_subtitle || "Tradicion y confianza"}</span>
              <h2 className="heading-industrial text-2xl sm:text-3xl font-bold text-white mt-3 uppercase">{content.trust_section_title || "Más de cuatro décadas cuidando de tu vehículo"}</h2>
            </div>
            <div className={`grid gap-6 ${trustStats.length <= 4 ? "grid-cols-2 lg:grid-cols-4" : "grid-cols-2 lg:grid-cols-5"}`}>
              {trustStats.map((stat, i) => (
                <div key={i} className="text-center py-8 border border-slate-700/30 hover:border-red-500/30 transition-colors duration-500 group" data-testid={`trust-stat-${i}`}>
                  <p className="heading-industrial text-4xl sm:text-5xl font-extrabold text-red-500 group-hover:text-red-400 transition-colors">{stat.value}</p>
                  <p className="text-sm font-bold text-white uppercase tracking-wider mt-3">{stat.label}</p>
                  {stat.description && <p className="text-xs text-slate-500 mt-1.5">{stat.description}</p>}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ============================================= */}
      {/* 6. VEHICULOS USADOS Y KM0                     */}
      {/* ============================================= */}
      {show("vehicles") && (
        <section className="py-24 lg:py-32 bg-white" data-testid="vehicles-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-5">
              <div>
                <span className="text-[11px] uppercase tracking-[0.3em] text-red-600 font-semibold">{content.vehicles_section_subtitle || "Stock seleccionado"}</span>
                <h2 className="heading-industrial text-2xl sm:text-3xl font-bold text-slate-900 mt-3 uppercase">{content.vehicles_section_title || "Vehículos usados y Km0 seleccionados"}</h2>
              </div>
              <Link to="/vehiculos" onClick={() => track("vehicles_see_all", "ver_stock")}>
                <Button variant="outline" className="rounded-none text-xs uppercase tracking-wider font-semibold h-10 px-6 border-slate-300 hover:bg-slate-900 hover:text-white hover:border-slate-900 transition-all duration-300" data-testid="vehicles-see-all">
                  Ver todo el stock <ArrowRight className="w-3.5 h-3.5 ml-2" />
                </Button>
              </Link>
            </div>
            <p className="text-sm text-slate-500 mb-12 max-w-2xl leading-relaxed">{content.vehicles_section_description || "No vendemos vehiculo nuevo. Seleccionamos unidades revisadas, garantizadas y listas para entregar."}</p>

            {vehicles.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {vehicles.map((v) => (
                  <Link to={`/vehiculos/${v.id}`} key={v.id} className="block group" onClick={() => track("vehicle_click", `${v.brand} ${v.model}`)} data-testid={`home-vehicle-link-${v.id}`}>
                    <Card className="border-slate-200 rounded-none overflow-hidden hover:shadow-lg hover:border-slate-300 transition-all duration-300 cursor-pointer" data-testid={`home-vehicle-${v.id}`}>
                      <div className="h-48 bg-slate-100 relative overflow-hidden">
                        {v.images?.length > 0 ? (
                          <img src={`${API}/api/files/${v.images[0].path}`} alt={`${v.brand} ${v.model}`} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center"><Car className="w-14 h-14 text-slate-300" /></div>
                        )}
                        <Badge className={`absolute top-3 left-3 rounded-none text-[10px] uppercase font-bold tracking-wider ${v.vehicle_type === "km0" ? "bg-red-600 text-white" : "bg-slate-900 text-white"}`}>
                          {v.vehicle_type === "km0" ? "KM0" : "Ocasion"}
                        </Badge>
                        <div className="absolute bottom-3 left-3 flex gap-1.5">
                          <span className="bg-white/90 backdrop-blur-sm text-[9px] font-bold text-slate-700 uppercase px-2 py-0.5">Revisado</span>
                          <span className="bg-white/90 backdrop-blur-sm text-[9px] font-bold text-slate-700 uppercase px-2 py-0.5">Garantizado</span>
                        </div>
                      </div>
                      <CardContent className="p-5">
                        <h3 className="heading-industrial text-base font-bold text-slate-900 uppercase group-hover:text-red-600 transition-colors">{v.brand} {v.model}</h3>
                        <p className="text-xl font-bold text-red-600 font-mono mt-1">{v.price?.toLocaleString("es-ES")} &euro;</p>
                        <div className="flex items-center gap-4 mt-2 text-xs text-slate-500">
                          <span className="flex items-center gap-1"><Gauge className="w-3.5 h-3.5" />{v.mileage?.toLocaleString("es-ES")} km</span>
                          <span className="flex items-center gap-1"><Fuel className="w-3.5 h-3.5" />{v.fuel_type}</span>
                          <span className="flex items-center gap-1"><Settings2 className="w-3.5 h-3.5" />{v.year}</span>
                        </div>
                        <div className="flex gap-1.5 mt-3">
                          <span className="text-[9px] font-semibold uppercase text-slate-400 border border-slate-200 px-1.5 py-0.5">Entrega inmediata</span>
                          <span className="text-[9px] font-semibold uppercase text-slate-400 border border-slate-200 px-1.5 py-0.5">Financiacion</span>
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-20 border border-dashed border-slate-300 bg-slate-50/50">
                <Car className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <h3 className="heading-industrial text-xl font-bold text-slate-400 uppercase">Catalogo en preparacion</h3>
                <p className="text-sm text-slate-400 mt-2 max-w-md mx-auto">Estamos incorporando nuestro stock online. Consúltanos por teléfono o visítanos.</p>
                <Link to="/vehiculos" onClick={() => track("vehicles_empty_cta", "ir_catalogo")}><Button variant="outline" className="rounded-none text-xs uppercase tracking-wider font-semibold h-9 px-6 border-slate-300 mt-6">Ir al catalogo</Button></Link>
              </div>
            )}
          </div>
        </section>
      )}

      {/* ============================================= */}
      {/* 7. OPINIONES DE CLIENTES                      */}
      {/* ============================================= */}
      {show("testimonials") && testimonials.length > 0 && (
        <section className="py-24 lg:py-32 bg-slate-50" data-testid="testimonials-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <span className="text-[11px] uppercase tracking-[0.3em] text-red-600 font-semibold">{content.testimonials_section_subtitle || "Opiniones reales"}</span>
              <h2 className="heading-industrial text-2xl sm:text-3xl font-bold text-slate-900 mt-3 uppercase">{content.testimonials_section_title || "Lo que dicen nuestros clientes"}</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {testimonials.sort((a, b) => (a.order || 0) - (b.order || 0)).map((t, i) => (
                <div key={i} className="bg-white border border-slate-200 p-7 hover:border-slate-300 hover:shadow-sm transition-all duration-300" data-testid={`testimonial-card-${i}`}>
                  <div className="flex gap-0.5 mb-4">
                    {Array.from({ length: t.rating || 5 }).map((_, j) => <Star key={j} className="w-4 h-4 fill-amber-400 text-amber-400" />)}
                  </div>
                  <Quote className="w-5 h-5 text-slate-200 mb-3" />
                  <p className="text-sm text-slate-600 leading-relaxed mb-6">{t.text}</p>
                  <div className="border-t border-slate-100 pt-4">
                    <p className="text-sm font-semibold text-slate-900">{t.name}</p>
                    <p className="text-xs text-slate-400 mt-0.5">{t.location}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ============================================= */}
      {/* PROMOCIONES ACTIVAS                            */}
      {/* ============================================= */}
      {show("promotions") && promotions.length > 0 && (
        <section className="py-24 lg:py-32 bg-white" data-testid="promotions-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <span className="text-[11px] uppercase tracking-[0.3em] text-red-600 font-semibold">{content.promotions_section_subtitle || "Ofertas activas"}</span>
              <h2 className="heading-industrial text-2xl sm:text-3xl font-bold text-slate-900 mt-3 uppercase">{content.promotions_section_title || "Promociones Postventa"}</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {promotions.map((p, i) => {
                const imgSrc = getImageUrl(p.image_url);
                const promoParams = new URLSearchParams({
                  promo_id: p.id || "",
                  promo_titulo: p.title || "",
                  promo_categoria: p.category || "",
                  promo_precio: p.highlight_text || "",
                  promo_origen: "promocion_postventa",
                }).toString();
                return (
                  <div key={p.id || i}
                    className="group border border-slate-200 hover:border-slate-300 hover:shadow-md transition-all duration-300 overflow-hidden flex flex-col"
                    data-testid={`promo-card-${i}`}>
                    {p.image_url && (
                      <div className="aspect-[16/9] overflow-hidden bg-slate-100">
                        <img src={imgSrc} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                      </div>
                    )}
                    <div className="p-5 flex-1 flex flex-col">
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wide">{p.title}</h3>
                        {p.highlight_text && <span className="text-[9px] uppercase tracking-wider font-bold bg-red-600 text-white px-2 py-0.5 rounded-sm whitespace-nowrap">{p.highlight_text}</span>}
                      </div>
                      {p.description && <p className="text-xs text-slate-500 leading-relaxed">{p.description}</p>}
                      {p.end_date && (
                        <p className="text-[10px] text-slate-400 mt-3 flex items-center gap-1">
                          <Clock className="w-3 h-3" /> Hasta el {new Date(p.end_date).toLocaleDateString("es-ES", { day: "numeric", month: "long" })}
                        </p>
                      )}
                      <div className="mt-auto pt-4">
                        <Link to={`/cita?${promoParams}`}
                          className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white text-xs font-bold uppercase tracking-wider px-4 py-2.5 transition-colors w-full justify-center"
                          data-testid={`promo-cta-${i}`}
                          onClick={() => track("promo_book_click", p.title)}>
                          <CalendarCheck className="w-3.5 h-3.5" /> Reservar cita con esta oferta
                        </Link>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* ============================================= */}
      {/* 8. SEDES Y CONTACTO                           */}
      {/* ============================================= */}
      {show("centers") && centers.length > 0 && (
        <section className="py-24 lg:py-32 bg-white" data-testid="centers-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <span className="text-[11px] uppercase tracking-[0.3em] text-red-600 font-semibold">{content.centers_section_subtitle || "Visitanos"}</span>
              <h2 className="heading-industrial text-2xl sm:text-3xl font-bold text-slate-900 mt-3 uppercase">{content.centers_section_title || "Nuestros Centros"}</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
              {centers.map((c, i) => (
                <div key={c.id} className="bg-white border border-slate-200 overflow-hidden hover:shadow-lg transition-all duration-300 group" data-testid={`center-card-${i}`}>
                  <div className="h-52 overflow-hidden relative bg-slate-200">
                    {(c.image_url || c.map_image_url) ? (
                      <img src={c.image_url || c.map_image_url} alt={c.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                    ) : (
                      <div className="w-full h-full bg-slate-900 flex items-center justify-center"><Building2 className="w-16 h-16 text-slate-700" /></div>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 via-slate-900/30 to-transparent" />
                    <div className="absolute bottom-5 left-6">
                      <h3 className="heading-industrial text-xl sm:text-2xl font-bold text-white uppercase">{c.name}</h3>
                    </div>
                  </div>
                  <div className="p-6 lg:p-7">
                    <div className="space-y-3 mb-6">
                      <div className="flex items-start gap-3">
                        <MapPin className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="text-sm font-medium text-slate-900">{c.address}</p>
                          <p className="text-xs text-slate-500">{c.zip_code} {c.city}</p>
                        </div>
                      </div>
                      {c.phone && (
                        <div className="flex items-center gap-3">
                          <Phone className="w-4 h-4 text-red-600 flex-shrink-0" />
                          <a href={`tel:${c.phone.replace(/\s/g, "")}`} onClick={() => track("phone_call", c.name)} className="text-sm text-slate-700 hover:text-red-600 transition-colors font-medium">{c.phone}</a>
                        </div>
                      )}
                      <div className="flex items-center gap-3">
                        <Clock className="w-4 h-4 text-red-600 flex-shrink-0" />
                        <p className="text-sm text-slate-600">{formatHours(c.working_hours)}</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <Link to="/cita" onClick={() => track("center_cta_cita", c.name)}>
                        <Button className="w-full bg-slate-900 hover:bg-slate-800 text-white rounded-none text-[10px] uppercase tracking-wider font-bold h-10 transition-colors" data-testid={`center-cta-${i}`}>
                          <CalendarCheck className="w-3.5 h-3.5 mr-1.5" /> Pedir cita
                        </Button>
                      </Link>
                      <a href={c.google_maps_url || `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${c.address} ${c.zip_code} ${c.city}`)}`} target="_blank" rel="noopener noreferrer" onClick={() => track("center_directions", c.name)}>
                        <Button variant="outline" className="w-full border-slate-300 text-slate-700 rounded-none text-[10px] uppercase tracking-wider font-bold h-10 hover:bg-slate-50 transition-colors" data-testid={`center-directions-${i}`}>
                          <MapPin className="w-3.5 h-3.5 mr-1.5" /> Como llegar
                        </Button>
                      </a>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ============================================= */}
      {/* CONOCENOS — BLOQUE RESUMEN                    */}
      {/* ============================================= */}
      {show("conocenos") && (() => {
        const cb = content.conocenos_home_block || {};
        return (
          <section className="py-24 lg:py-32 bg-slate-50" data-testid="conocenos-home-section">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
                <div>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-8 h-1 bg-red-600" />
                    <span className="text-red-600 text-[11px] font-semibold uppercase tracking-[0.25em]">
                      {cb.label || "Sobre nosotros"}
                    </span>
                  </div>
                  <h2 className="heading-industrial text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900 uppercase mb-6">
                    {cb.title || `Conoce ${companyName}`}
                  </h2>
                  <p className="text-sm sm:text-base text-slate-600 leading-relaxed mb-8">
                    {cb.text || `Somos un grupo de talleres oficiales con mas de 40 anos de experiencia. Descubre nuestra historia, nuestros centros y nuestra forma de trabajar.`}
                  </p>
                  <Link to="/conocenos" onClick={() => track("home_conocenos", "ver_mas")}>
                    <Button className="bg-red-600 hover:bg-red-700 text-white rounded-none text-sm uppercase tracking-wider font-bold h-12 px-8 shadow-lg shadow-red-600/20 transition-all hover:shadow-xl" data-testid="home-conocenos-btn">
                      {cb.button_text || "Conócenos"} <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </Link>
                </div>
                <div className="relative">
                  {cb.image_url ? (
                    <div className="aspect-[4/3] overflow-hidden">
                      <img
                        src={getImageUrl(cb.image_url)}
                        alt={cb.title || "Conócenos"}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  ) : (
                    <div className="aspect-[4/3] bg-slate-200 flex items-center justify-center">
                      <Building2 className="w-24 h-24 text-slate-300" />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </section>
        );
      })()}

      {/* ============================================= */}
      {/* 9. CTA FINAL                                  */}
      {/* ============================================= */}
      {show("cta_final") && (
        <section className="py-24 lg:py-32 bg-slate-900 relative overflow-hidden" data-testid="final-cta-section">
          <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: "repeating-linear-gradient(90deg, rgba(255,255,255,.07) 0 1px, transparent 1px 60px)" }} />
          <div className="relative z-10 max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="w-16 h-16 bg-red-600 flex items-center justify-center mx-auto mb-8">
              <CalendarCheck className="w-8 h-8 text-white" />
            </div>
            <h2 className="heading-industrial text-2xl sm:text-3xl lg:text-4xl font-bold text-white uppercase mb-5">
              {ctaFinal.title || "Necesitas revisar tu vehiculo?"}
            </h2>
            <p className="text-slate-400 mb-12 max-w-lg mx-auto text-sm sm:text-base leading-relaxed">
              {ctaFinal.subtitle || "Solicita tu cita online en cualquiera de nuestros centros. Te confirmamos en menos de 24 horas."}
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/cita" onClick={() => track("final_cta", "reservar_cita")}>
                <Button className="bg-red-600 hover:bg-red-700 text-white rounded-none text-sm uppercase tracking-wider font-bold h-14 px-12 shadow-lg shadow-red-600/30 transition-all hover:shadow-xl" data-testid="final-cta-btn">
                  {ctaFinal.button_text || "Reservar Cita Ahora"} <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
              {waPhoneClean && (
                <a href={buildWaHref()} target="_blank" rel="noopener noreferrer"
                  onClick={() => track("final_cta_whatsapp", "whatsapp")}
                  className="inline-flex items-center justify-center gap-2 border border-slate-600 text-slate-300 hover:bg-white/5 hover:border-slate-400 rounded-none text-sm uppercase tracking-wider font-bold h-14 px-10 transition-all"
                  data-testid="final-cta-whatsapp">
                  <MessageCircle className="w-5 h-5" /> WhatsApp
                </a>
              )}
            </div>
          </div>
        </section>
      )}

      {/* ============================================= */}
      {/* 10. FLOATING CTAs                             */}
      {/* ============================================= */}
      {(() => { const fc = content.floating_ctas || {}; return (
        <div className="fixed bottom-6 right-6 z-50 flex flex-col gap-3" data-testid="floating-ctas">
          {waPhoneClean && fc.whatsapp !== false && (
            <a
              href={buildWaHref()}
              target="_blank" rel="noopener noreferrer"
              onClick={() => track("floating_whatsapp", "click")}
              className="w-14 h-14 bg-[#25D366] hover:bg-[#1EBE5A] text-white rounded-full flex items-center justify-center shadow-lg shadow-green-600/30 hover:scale-110 transition-all duration-300"
              data-testid="floating-whatsapp" title={fc.whatsapp_label || "WhatsApp"}
            >
              <svg viewBox="0 0 24 24" fill="currentColor" className="w-7 h-7"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" /></svg>
            </a>
          )}
          {fc.cita !== false && (
            <Link
              to="/cita"
              onClick={() => track("floating_cita", "click")}
              className="w-14 h-14 bg-red-600 hover:bg-red-700 text-white rounded-full flex items-center justify-center shadow-lg shadow-red-600/30 hover:scale-110 transition-all duration-300"
              data-testid="floating-cita" title={fc.cita_label || "Pedir cita"}
            >
              <CalendarCheck className="w-6 h-6" />
            </Link>
          )}
        </div>
      ); })()}
    </div>
  );
}
